'use client';

import { useCallback, useEffect, useRef, type ReactNode } from 'react';
import { useAtom } from 'jotai';

import { cn } from '@packages/shared/src/utils/cn/cn';

import { useClickAway } from '@packages/shared/src/hooks/useClickAway/useClickAway';
import { isMobileSearchVisibleAtom } from './Header.atoms';
import { useSearchQuery } from './useSearchQuery';
import { SEARCH_SLOT_ID } from './Header.ids';

export type SearchFieldSlotProps = {
  isMobileSearchAlwaysVisible: boolean;
};

export type ResponsiveHeaderSearchWrapperProps = {
  children: ReactNode;
};

/**
 * This client-side component handles the (considerable) complexity of interactions the search field currently has,
 * especially on mobile devices. For backwards compatibility, we have to support all previous behaviour.
 *
 * - On desktop, the search field is always visible
 * - On mobile, if not on a SERP, the search field is toggled by a button
 *   - The button is always visible
 *   - The search field has a partial backdrop that doesn't cover the header
 *   - The search field is closed by clicking outside of it
 *   - Clicking outside of it in the header also instantly activates whatever was clicked on
 *   - Clicking on the backdrop consumes the click, and closes the search field
 * - On mobile, the search field is always visible on the SERP
 * */
export const ResponsiveHeaderSearchWrapper = ({ children }: ResponsiveHeaderSearchWrapperProps) => {
  const searchQuery = useSearchQuery();
  const alwaysShowMobileSearch = !!searchQuery; // we are on a SERP if a query exists

  const [isMobileSearchVisible, setIsMobileSearchVisible] = useAtom(isMobileSearchVisibleAtom);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const backdropRef = useRef<HTMLDivElement | null>(null);

  // This effect is used to position the backdrop correctly, making it cover the rest of the page, but not the header and anything header-related
  // It would be a lot simpler if the search field were allowed to be a native dialog, or just in the page flow
  // For legacy reasons, it currently has some in-between behaviour, which is quite complex to implement
  useEffect(() => {
    if (!isMobileSearchVisible || !containerRef.current) return;

    const { top, height } = containerRef.current.getBoundingClientRect();

    backdropRef.current?.style.setProperty('--top', `${top + height}px`);

    // autofocus for user convenience when opening the search field
    containerRef.current.querySelector('input')?.focus();
  }, [isMobileSearchVisible]);

  const showMobileSearch = isMobileSearchVisible || alwaysShowMobileSearch;

  const onClickAway = useCallback(
    (event: MouseEvent | TouchEvent) => {
      if (
        // when clicking on the search slot button, we don't want to close the search field, otherwise it will instantly re-open again from the button click
        // we can't blanket-prevent/stop the event, because it may be a click on some other part of the header
        !document.getElementById(SEARCH_SLOT_ID)?.contains(event.target as Node) &&
        // similarly, when not excluding the backdrop, the backdrop won't consume click events, because the click-away triggers on mousedown, and the backdrop is removed before the click event happens
        !backdropRef.current?.contains(event.target as Node)
      ) {
        setIsMobileSearchVisible(false);
      }
    },
    [setIsMobileSearchVisible],
  );

  useClickAway(containerRef, { onClickAway });

  return (
    <div className="relative [grid-area:nav] lg:[grid-area:search]">
      {/* escape key to close the search field is on this div, to capture all bubbling key presses anywhere inside */}
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div
        ref={containerRef}
        className={cn(
          'z-searchFieldPopup max-lg:bg-color-grey-light max-lg:z-tooltip max-lg:flex max-lg:flex-col max-lg:px-1.5 max-lg:py-1 lg:mx-3 xl:mx-6',
          !showMobileSearch && 'max-lg:hidden',
          isMobileSearchVisible &&
            !alwaysShowMobileSearch &&
            'max-lg:absolute max-lg:top-0 max-lg:right-0 max-lg:left-0',
        )}
        onKeyDown={(event) => {
          // support ergonomic keyboard usage by closing the search field with the escape key, and focusing the search slot button
          // this brings the user more or less back to the same state as before opening the search field
          if (event.key === 'Escape') {
            setIsMobileSearchVisible(false);

            const searchSlot = document.getElementById(SEARCH_SLOT_ID);

            if (searchSlot instanceof HTMLButtonElement) {
              searchSlot?.focus();
            }
          }
        }}
      >
        {children}
      </div>

      {/* backdrop intercepts click events, but is not strictly speaking interactive */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        ref={backdropRef}
        className={cn(
          'bg-color-backdrop z-searchFieldPopup fixed inset-0 top-[var(--top)] lg:hidden',
          (!showMobileSearch || alwaysShowMobileSearch) && 'max-lg:hidden',
        )}
        onClick={() => setIsMobileSearchVisible(false)}
      />
    </div>
  );
};
